<template>
  <InputWrapper
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    input-id="purchase-cargo-insurance-commodity-id"
    :label="t('cargoInsurance.commodity')"
    class="col-12"
    :errors="commodityErrors"
  >
    <RygenMultiSelect
      v-bind="ariaProps"
      :id="inputId"
      :model-value="commodities"
      :class="inputClasses"
      :invalid="invalid"
      :options="availableCommodities.data"
      :loading="availableCommodities.loading"
      data-key="id"
      option-label="name"
      filter
      show-clear
      append-to="body"
      :placeholder="t('cargoInsurance.commodity')"
      @update:model-value="onCommoditiesChange"
    />
  </InputWrapper>

  <InputWrapper
    v-if="miscCommodity"
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    input-id="purchase-cargo-insurance-commodity-misc-desc"
    :label="t('cargoInsurance.quote.commodityMisc')"
    :help-text="t('cargoInsurance.quote.commodityMiscHelpText')"
    class="col-12"
    :errors="commodityMiscNameErrors"
  >
    <InputText
      :id="inputId"
      v-model="miscCommodity.name"
      v-bind="ariaProps"
      :class="inputClasses"
      :invalid="invalid"
      :placeholder="t('cargoInsurance.quote.commodityMisc')"
    />
  </InputWrapper>
</template>

<script setup lang="ts">
import type { ErrorObject } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import axios from 'axios';
import { computed, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useToast,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CargoInsuranceProviderCommodity } from '@/model';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import InputText from 'primevue/inputtext';

const commodities = defineModel<CargoInsuranceProviderCommodity[]>({
  required: true,
});

const props = defineProps<{
  shipmentId: number;
}>();

const { shipmentId } = toRefs(props);

const { t } = useI18n();

const toast = useToast();

const rules = {
  commodities: {
    required: required(t('cargoInsurance.commodity')),
    $each: helpers.forEach({
      name: {
        required: helpers.withMessage(
          t('validations.required', {
            label: t('cargoInsurance.quote.commodityMisc'),
          }),
          required(),
        ),
      },
      id: {
        required: required(t('cargoInsurance.commodity')),
      },
    }),
  },
};
const validationObject = computed(() => ({ commodities: commodities.value }));
const { v$ } = useValidations(validationObject, rules);

const availableCommodities: Ref<Loadable<CargoInsuranceProviderCommodity[]>> =
  ref({ loading: false, data: [] });

const MISC_COMMODITY_ID = 1;

const miscCommodity = computed(() => {
  return commodities.value.find((c) => {
    // type inference needed since id can be number or string
    return c.id === MISC_COMMODITY_ID;
  });
});

const commodityErrors = computed(() => {
  return (
    v$.value.commodities?.$errors?.filter(
      (e: ErrorObject) => e.$uid === 'commodities-required',
    ) || []
  );
});

const commodityMiscNameErrors = computed(() => {
  return (
    v$.value.commodities?.$each?.$response?.$errors?.reduce(
      (acc: ErrorObject[], e: Record<string, ErrorObject[]>) => {
        acc = [...acc, ...e.name];
        return acc;
      },
      [] as ErrorObject[],
    ) || []
  );
});

function onCommoditiesChange(newVal: CargoInsuranceProviderCommodity[]) {
  const miscCommodityName = miscCommodity.value?.name;

  // copy the commodity, but remove the name if it's the misc commodity
  commodities.value = newVal.map((c) => {
    const isMiscCommodity = c.id === MISC_COMMODITY_ID;
    return {
      ...c,
      ...{ name: isMiscCommodity ? miscCommodityName : c.name },
    };
  });
}

async function fetchCommodities(shipmentId: number) {
  availableCommodities.value.data = [];
  try {
    availableCommodities.value.loading = true;
    availableCommodities.value.data =
      await api.shipmentCargoInsurance.getCommodities(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading commodities', e);
    availableCommodities.value.error = e as Error;
    toast.addError({
      error: e,
      detail: t('errors.loadingParam', {
        param: t('cargoInsurance.commodity', 2),
      }),
    });
  } finally {
    availableCommodities.value.loading = false;
  }
}

watch(shipmentId, () => fetchCommodities(shipmentId.value), {
  immediate: true,
});
</script>
